import { z } from 'zod';
import { FLEET_RECOVERY_MAX_ITEMS, FleetRecoveryPathPolicy, fleetRecoveryIdSchema,
  fleetRecoveryPathSchema, fleetRecoveryRegistrySchema, fleetRecoverySha256Schema,
  isCanonicalBase64 } from './scalars';

const text = z.string().max(32767).refine(value => !value.includes('\0'));
const argumentsSchema = z.array(z.string().min(1).max(256).regex(/^[A-Za-z0-9_./:=\-]+$/)).max(16);
const hardwareId = z.string().min(1).max(256).regex(/^[A-Za-z0-9_\\&{}#.-]+$/);
const baseConfigurationSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('file'), version: z.string().min(1).max(128).nullable() }).strict(),
  z.object({ kind: z.literal('registry'), view: z.enum(['32', '64']), valueName: text,
    valueType: z.enum(['REG_SZ', 'REG_EXPAND_SZ', 'REG_MULTI_SZ', 'REG_DWORD', 'REG_BINARY']),
    value: z.union([text, z.array(text).max(FLEET_RECOVERY_MAX_ITEMS), z.number().int().min(0).max(0xffffffff)]),
    edit: z.enum(['owned-value', 'owned-multi-sz-element']) }).strict(),
  z.object({ kind: z.literal('service'), executablePath: fleetRecoveryPathSchema, arguments: argumentsSchema,
    account: z.enum(['LocalSystem', 'NT AUTHORITY\\LocalService']),
    startMode: z.enum(['automatic', 'demand', 'disabled']) }).strict(),
  z.object({ kind: z.literal('task'), executablePath: fleetRecoveryPathSchema, arguments: argumentsSchema,
    account: z.literal('LocalSystem'), bootTrigger: z.boolean(), intervalSeconds: z.number().int().min(1).max(86400),
    executionLimitSeconds: z.number().int().min(1).max(86400), instancePolicy: z.literal('StopExisting') }).strict(),
  z.object({ kind: z.literal('device'), hardwareId,
    expectedDriverTargetId: fleetRecoveryIdSchema }).strict(),
  z.object({ kind: z.literal('driver'), infSha256: fleetRecoverySha256Schema,
    catalogSha256: fleetRecoverySha256Schema, driverVersion: z.string().regex(/^\d+\.\d+\.\d+\.\d+$/),
    hardwareIds: z.array(hardwareId).max(FLEET_RECOVERY_MAX_ITEMS) }).strict(),
]);

export const targetConfigurationSchema = baseConfigurationSchema.superRefine((configuration, context) => {
  if (configuration.kind !== 'registry') return;
  const { valueType, value } = configuration;
  const matchesType = valueType === 'REG_DWORD' ? typeof value === 'number'
    : valueType === 'REG_MULTI_SZ' ? Array.isArray(value)
    : typeof value === 'string' && (valueType !== 'REG_BINARY' || isCanonicalBase64(value));
  if (!matchesType) context.addIssue({ code: 'custom', path: ['value'], message: 'Registry value/type mismatch' });
  if (configuration.edit === 'owned-multi-sz-element' && valueType !== 'REG_MULTI_SZ') {
    context.addIssue({ code: 'custom', path: ['edit'], message: 'Element edit requires MULTI_SZ' });
  }
});

export const ownedTargetSchema = z.object({
  id: fleetRecoveryIdSchema, kind: z.enum(['file', 'registry', 'service', 'task', 'device', 'driver']),
  scope: z.enum(['product', 'recovery']), locator: z.string().min(1).max(32767),
  ownershipRuleId: fleetRecoveryIdSchema, desired: z.enum(['present', 'absent']),
  required: z.boolean(), expectedSha256: fleetRecoverySha256Schema.nullable(),
  configuration: targetConfigurationSchema.nullable(),
}).strict().superRefine((target, context) => {
  const fail = (path: string, message: string) => context.addIssue({ code: 'custom', path: [path], message });
  if ((target.desired === 'present') !== (target.configuration !== null)) fail('configuration', 'Present requires configuration; absent requires null');
  if (target.configuration && target.configuration.kind !== target.kind) fail('configuration', 'Configuration kind differs from target');
  const validLocator = target.kind === 'file' ? fleetRecoveryPathSchema.safeParse(target.locator).success
    : target.kind === 'registry' ? fleetRecoveryRegistrySchema.safeParse(target.locator).success
    : target.kind === 'task' ? /^\\?[A-Za-z0-9][A-Za-z0-9_.\\-]{0,255}$/.test(target.locator)
    : target.kind === 'device' ? hardwareId.safeParse(target.locator).success
    : /^[A-Za-z0-9][A-Za-z0-9_.-]{0,255}$/.test(target.locator);
  if (!validLocator) fail('locator', 'Noncanonical target locator');
  if ((target.scope === 'recovery') !== FleetRecoveryPathPolicy.isRecoveryLocator(target.locator)) fail('scope', 'Target scope differs from independent recovery boundary');
  const configuration = target.configuration;
  if (!configuration) return;
  if (configuration.kind === 'driver' && !configuration.hardwareIds.length
    && !['keyboardcaster.inf', 'mousecaster.inf'].includes(target.locator.toLowerCase())) {
    fail('configuration', 'Only the exact reviewed class-filter packages may omit PnP hardware IDs');
  }
  if (configuration.kind === 'service' || configuration.kind === 'task') {
    if ((target.scope === 'recovery') !== FleetRecoveryPathPolicy.isRecoveryLocator(configuration.executablePath)) {
      fail('configuration', 'Executable crosses scope boundary');
    }
    if (target.scope === 'recovery') validateRecoveryEntry(target.locator, configuration, fail);
  }
  if (target.scope === 'recovery' && (!target.required || (target.kind === 'file' && !target.expectedSha256))) {
    fail('required', 'Recovery resources require verified identity and mandatory readiness');
  }
});

function validateRecoveryEntry(locator: string, configuration: z.infer<typeof baseConfigurationSchema>, fail: (path: string, message: string) => void): void {
  if (configuration.kind !== 'service' && configuration.kind !== 'task') return;
  const name = configuration.executablePath.split('\\').pop();
  const args = configuration.arguments;
  const validService = name === 'PlaycastUpdateService.exe' && args.length === 1 && args[0] === '--service'
    || name === 'PlaycastRecoveryWorker.exe' && args.length === 3 && args[0] === '--service'
      && args[1] === '--operation-id' && z.string().uuid().safeParse(args[2]).success;
  const validTask = (name === 'PlaycastRescue.exe' || name === 'PlaycastRescueBaseline.exe')
    && args.length === 2 && args[0] === '--check-and-restore' && args[1] === '--json';
  if (configuration.account !== 'LocalSystem' || !(configuration.kind === 'service' ? validService : validTask)) {
    fail('configuration', 'Recovery action must match fixed catalogued SYSTEM entry mode');
  }
  if (configuration.kind === 'task' && (!configuration.bootTrigger || configuration.intervalSeconds !== 60 || configuration.executionLimitSeconds !== 55)) {
    fail('configuration', 'Recovery task requires boot + 60 second cadence and 55 second limit');
  }
  if (configuration.kind === 'service' && configuration.startMode === 'disabled') fail('configuration', 'Recovery service cannot be disabled');
  if (locator === 'PlaycastUpdateService' && name !== 'PlaycastUpdateService.exe') fail('configuration', 'Updater service image mismatch');
}

export const ownershipRuleSchema = z.object({
  id: fleetRecoveryIdSchema, publishers: z.array(fleetRecoveryIdSchema).max(FLEET_RECOVERY_MAX_ITEMS),
  knownSha256: z.array(fleetRecoverySha256Schema).max(FLEET_RECOVERY_MAX_ITEMS),
  canonicalRoots: z.array(z.union([fleetRecoveryPathSchema, fleetRecoveryRegistrySchema])).max(FLEET_RECOVERY_MAX_ITEMS),
  registryView: z.enum(['32', '64', 'both']).nullable(), hardwareIds: z.array(hardwareId).max(FLEET_RECOVERY_MAX_ITEMS),
  infSha256: z.array(fleetRecoverySha256Schema).max(FLEET_RECOVERY_MAX_ITEMS),
  catalogSha256: z.array(fleetRecoverySha256Schema).max(FLEET_RECOVERY_MAX_ITEMS), shared: z.boolean(),
}).strict().refine(rule => rule.canonicalRoots.length + rule.knownSha256.length + rule.hardwareIds.length
  + rule.infSha256.length + rule.catalogSha256.length > 0, 'Ownership requires positive identity beyond a provider/name');
